'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  rating: number;
  text: string;
  savings: string;
  timeframe: string;
}

const TestimonialsSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Katarzyna Wiśniewska',
    role: 'Klientka indywidualna',
    company: 'Kredyt hipoteczny',
    rating: 5,
    text: 'Kupno pierwszego mieszkania to był dla mnie stres. Nasza Hipoteka przeprowadziła mnie przez cały proces, znalazła najlepszą ofertę hipoteczną i zaoszczędziła mi 85 000 PLN. Polecam każdemu!',
    savings: '85 000 PLN',
    timeframe: '5 dni'
  },
  {
    id: '2',
    name: 'Marek Kowalski',
    role: 'Klient indywidualny',
    company: 'Kredyt hipoteczny',
    rating: 5,
    text: 'Potrzebowałem kredytu hipotecznego na zakup domu. Nasza Hipoteka wynegocjowała dla mnie oprocentowanie o 0,5% niższe niż oferował mi bank. To oszczędność ponad 90 000 PLN w ciągu całego okresu kredytowania!',
    savings: '90 000 PLN',
    timeframe: '3 dni'
  },
  {
    id: '3',
    name: 'Anna Nowak',
    role: 'Klientka indywidualna',
    company: 'Kredyt hipoteczny',
    rating: 5,
    text: 'Dzięki Nasza Hipoteka otrzymałam kredyt hipoteczny na zakup mieszkania w zaledwie 4 dni. Zaoszczędziłam 75 000 PLN na odsetkach przez cały okres kredytowania. Profesjonalna obsługa i pełne wsparcie na każdym etapie.',
    savings: '75 000 PLN',
    timeframe: '4 dni'
  }];


  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Opinie klientów
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Zobacz, co mówią o nas zadowoleni klienci
            </p>
          </div>
          <div className="bg-card rounded-2xl h-96 animate-pulse" />
        </div>
      </section>);

  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
            <Icon name="StarIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-body font-medium text-accent">
              Opinie
            </span>
          </div>

          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Opinie klientów
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Zobacz, co mówią o nas zadowoleni klienci, którzy zaoszczędzili czas i pieniądze
          </p>
        </div>

        <div className="relative bg-card rounded-2xl shadow-card p-8 lg:p-12">

          <div className="relative">
            <div className="flex flex-col lg:flex-row items-center gap-8 mb-8">

              <div className="flex-1 text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-1 mb-2">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, i) =>
                  <Icon key={i} name="StarIcon" size={20} variant="solid" className="text-accent" />
                  )}
                </div>

                <h3 className="text-xl font-headline font-bold text-foreground mb-1">
                  {testimonials[activeTestimonial].name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {testimonials[activeTestimonial].role} • {testimonials[activeTestimonial].company}
                </p>
              </div>

              <div className="flex gap-4">
                <div className="bg-success/10 rounded-lg px-4 py-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Oszczędności</p>
                  <p className="text-lg font-headline font-bold text-success">
                    {testimonials[activeTestimonial].savings}
                  </p>
                </div>
                <div className="bg-primary/10 rounded-lg px-4 py-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Czas realizacji</p>
                  <p className="text-lg font-headline font-bold text-primary">
                    {testimonials[activeTestimonial].timeframe}
                  </p>
                </div>
              </div>
            </div>

            <blockquote className="text-lg text-foreground leading-relaxed mb-8">
              "{testimonials[activeTestimonial].text}"
            </blockquote>

            <div className="flex justify-center gap-2">
              {testimonials.map((_, index) =>
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-250 ${
                activeTestimonial === index ? 'bg-primary w-8' : 'bg-border'}`
                }
                aria-label={`Opinia ${index + 1}`} />

              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="bg-card rounded-xl shadow-card p-6 text-center">
            <div className="flex items-center justify-center w-14 h-14 bg-success/10 rounded-full mx-auto mb-4">
              <Icon name="UserGroupIcon" size={28} variant="outline" className="text-success" />
            </div>
            <p className="text-3xl font-headline font-bold text-foreground mb-2">5000+</p>
            <p className="text-sm text-muted-foreground">Zadowolonych klientów</p>
          </div>

          <div className="bg-card rounded-xl shadow-card p-6 text-center">
            <div className="flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mx-auto mb-4">
              <Icon name="StarIcon" size={28} variant="solid" className="text-accent" />
            </div>
            <p className="text-3xl font-headline font-bold text-foreground mb-2">4.9/5</p>
            <p className="text-sm text-muted-foreground">Średnia ocen</p>
          </div>

          <div className="bg-card rounded-xl shadow-card p-6 text-center">
            <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mx-auto mb-4">
              <Icon name="CurrencyDollarIcon" size={28} variant="outline" className="text-primary" />
            </div>
            <p className="text-3xl font-headline font-bold text-foreground mb-2">30%</p>
            <p className="text-sm text-muted-foreground">Średnie oszczędności</p>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;